import { HttpMethodEnum } from '@ipevo-rd/service-web-utils'
import { getHttpInstance } from '../../api.js'
import { ApiVersionEnum } from '../../enums/ApiVersionEnum.js'
import {
	adminHttpClient,
	handleAdminResponse,
	withTransportErrors,
} from './client.js'
import type {
	AdminLoginRequest,
	AdminLoginResult,
	TwoFaSetupResult,
	TwoFaVerifyRequest,
	TwoFaVerifyResult,
} from './dto.js'

export { AdminApiError } from './client.js'

/**
 * 登入二次驗證（POST /v1/admin/login）
 * - 此端點公開（不套 auth layer），token 放 body、不含 Bearer 前綴
 * - 不發新 token，只把手上的 MEB token 做二次驗證、確認帳號是 admin
 * - 非 admin 會回 code=1000004，函式以丟出 Error 表示，交由呼叫端攔截
 * - 略過全域認證錯誤處理，登入失敗只由呼叫端提示，不清狀態也不導頁
 * @param token MEB 登入拿到的 token（不含 Bearer 前綴）
 * @returns 驗證通過後的使用者資訊（user_id）
 */
export const adminLogin = async (token: string): Promise<AdminLoginResult> => {
	// 帶 token 打 admin 登入端點做二次驗證
	const body = await adminHttpClient.request<AdminLoginResult>(
		{
			url: '/admin/login',
			method: HttpMethodEnum.POST,
			data: { token } satisfies AdminLoginRequest,
		},
		{ skipAuthErrorHandlers: true },
	)

	// 回傳驗證通過後的使用者資訊
	return body.data
}

/**
 * 取得 2FA 綁定 QRCode URL（POST /v1/admin/2fa/setup）
 * - 需已登入（Authorization Bearer 由既有 V1 request interceptor 自動帶上 localStorage 的 token）
 * - 轉發至 MEB 產碼；已啟用 OTP 或帶正式 token 時 MEB 回 errno 1002，後端回 1000000，不給 secret，出處見 MEB `AdminUserService.php:217-220`、`:323-326`，分支 `origin/feature#1422-ha`
 * - 失敗（含非 admin 1000004）以丟出 Error 表示，交由呼叫端攔截
 * @returns otpauth:// 綁定 URL
 */
export const adminTwoFaSetup = async (): Promise<TwoFaSetupResult> => {
	// 打 2fa setup 端點取綁定 URL
	const body = await adminHttpClient.request<TwoFaSetupResult>({
		url: '/admin/2fa/setup',
		method: HttpMethodEnum.POST,
	})

	// 回綁定 URL 供前端畫 QRCode
	return body.data
}

/**
 * 驗證 2FA 6 位數碼（POST /v1/admin/2fa/verify）
 * - 需已登入，使用呼叫端捕捉的 token 固定 Authorization，避免攔截器切換成另一個分頁的新身分
 * - 後端純轉發 MEB 驗碼，通過後回 MEB 新發的正式 token（tfa=true），後續 admin 請求須改用它
 * - 失敗以丟出 AdminApiError 表示：1000009 碼錯可重試、1000010 已被鎖定、其餘見 body.message
 * @param code {string} 使用者在 Authenticator 看到的 6 位數碼
 * @param token {string} 送出驗證時固定使用的暫時 token
 * @returns {Promise<TwoFaVerifyResult>} 新發的正式 token（取代暫時 token）
 */
export const adminTwoFaVerify = async (
	code: string,
	token: string,
): Promise<TwoFaVerifyResult> => {
	// 建立獨立傳輸，避免共用攔截器重新讀取另一分頁的 token
	const verifyHttpClient = getHttpInstance(ApiVersionEnum.V1)
	// 以固定的暫時 token 打 2fa verify 端點
	const response = await withTransportErrors(() =>
		verifyHttpClient.request({
			url: '/admin/2fa/verify',
			method: HttpMethodEnum.POST,
			// 讓請求憑證與回應 session guard 使用同一顆 token
			headers: { Authorization: `Bearer ${token}` },
			data: { code } satisfies TwoFaVerifyRequest,
		}),
	)
	// 套用一致的回應檢查，僅在固定 token 仍是目前登入時觸發全域認證處理
	const body = handleAdminResponse<TwoFaVerifyResult>(response.data, {
		requestToken: token,
	})

	// 回新正式 token
	return body.data
}

/**
 * 登出（POST /v1/admin/logout）
 * - 以獨立傳輸固定撤銷指定的 token，不受共用攔截器之後換登入影響
 * - 後端轉發 MEB 登出，讓該 token 進黑名單
 * - 失敗（MEB 登出失敗等）會丟出 AdminApiError，由呼叫端依錯誤碼決定是否清本地狀態或提示重試
 * @param token {string} 本次要撤銷的 token
 * @returns {Promise<void>} 登出成功不回傳資料
 */
export const adminLogout = async (token: string): Promise<void> => {
	// 固定憑證登出使用獨立傳輸，沿用預設八十秒逾時
	const logoutHttpClient = getHttpInstance(ApiVersionEnum.V1)
	// 固定本次撤銷的憑證
	const response = await withTransportErrors(() =>
		logoutHttpClient.request({
			// 呼叫管理員登出端點
			url: '/admin/logout',
			// 以既有登出方法提交請求
			method: HttpMethodEnum.POST,
			// 固定帶入本次撤銷的憑證
			headers: { Authorization: `Bearer ${token}` },
		}),
	)
	// 獨立傳輸保留原始回應，仍沿用管理員業務錯誤語義
	handleAdminResponse<null>(response.data, {
		skipAuthErrorHandlers: true,
	})
}
